import { xMaxAbsoluteValue } from 'ml-spectra-processing';
import { getSumOfShapes } from "./shapes/getSumOfShapes.js";
import { buildOptimizationLayout } from "./util/buildOptimizationLayout.js";
import { getFixedParametersResult } from "./util/getFixedParametersResult.js";
import { getInternalPeaks } from "./util/internalPeaks/getInternalPeaks.js";
import { reconstructPeaks } from "./util/reconstructPeaks.js";
import { selectMethod } from "./util/selectMethod.js";
/**
 * Fits a set of points to the sum of a set of bell functions.
 * @param data - An object containing the x and y data to be fitted.
 * @param peaks - A list of initial parameters to be optimized. e.g. coming from a peak picking [{x, y, width}].
 * @param options - Options for optimize
 * @returns - An object with fitting error and the list of optimized parameters { parameters: [ {x, y, width} ], error } if the kind of shape is pseudoVoigt mu parameter is optimized.
 */
export function optimize(data, peaks, options = {}) {
    // rescale data so the maximum Y value becomes 1
    const max = xMaxAbsoluteValue(data.y);
    const yScale = max === 0 ? 1 : max;
    const internalPeaks = getInternalPeaks(peaks, yScale, options);
    // need to rescale what is related to Y
    const normalizedY = new Float64Array(data.y.length);
    for (let i = 0; i < data.y.length; i++) {
        normalizedY[i] = data.y[i] / yScale;
    }
    const optimizationLayout = buildOptimizationLayout(internalPeaks, peaks, options, yScale);
    const { freeIndices, variableMin, variableMax, variableInit, variableGrad, variables, } = optimizationLayout;
    const { algorithm, optimizationOptions } = selectMethod(options.optimization);
    const baseSumOfShapes = getSumOfShapes(internalPeaks);
    const sumOfShapesForVariables = (variableValues) => {
        return baseSumOfShapes(optimizationLayout.variableToPeakValues(variableValues));
    };
    if (freeIndices.length === 0) {
        return getFixedParametersResult(internalPeaks, normalizedY, data.x, optimizationLayout.variableToPeakValues(variableInit), baseSumOfShapes, yScale);
    }
    // prepare arrays to pass to the algorithm (reduced if needed)
    let minValues;
    let maxValues;
    let initialValues;
    let gradientDifferences;
    let sumOfShapesToUse = sumOfShapesForVariables;
    if (freeIndices.length === variables.length) {
        // nothing to reduce
        minValues = variableMin;
        maxValues = variableMax;
        initialValues = variableInit;
        gradientDifferences = variableGrad;
    }
    else {
        // wrapper that maps reduced (free) parameters into the full parameter vector
        const sumOfShapesForReduced = (reducedParameters) => {
            const full = new Float64Array(variables.length);
            full.set(variableInit);
            for (let k = 0; k < freeIndices.length; k++) {
                full[freeIndices[k]] = reducedParameters[k];
            }
            return sumOfShapesForVariables(full);
        };
        minValues = new Float64Array(freeIndices.length);
        maxValues = new Float64Array(freeIndices.length);
        initialValues = new Float64Array(freeIndices.length);
        gradientDifferences = new Float64Array(freeIndices.length);
        for (let j = 0; j < freeIndices.length; j++) {
            const i = freeIndices[j];
            minValues[j] = variableMin[i];
            maxValues[j] = variableMax[i];
            initialValues[j] = variableInit[i];
            gradientDifferences[j] = variableGrad[i];
        }
        sumOfShapesToUse = sumOfShapesForReduced;
    }
    const fitted = algorithm({ x: data.x, y: normalizedY }, sumOfShapesToUse, {
        minValues,
        maxValues,
        initialValues,
        gradientDifference: gradientDifferences,
        ...optimizationOptions,
    });
    let fittedVariableValues;
    if (freeIndices.length === variables.length) {
        fittedVariableValues = fitted.parameterValues;
    }
    else {
        const full = variableInit.slice();
        for (let k = 0; k < freeIndices.length; k++) {
            full[freeIndices[k]] = fitted.parameterValues[k];
        }
        fittedVariableValues = full;
    }
    const fittedValues = optimizationLayout.variableToPeakValues(fittedVariableValues);
    return {
        error: fitted.parameterError,
        iterations: fitted.iterations,
        peaks: reconstructPeaks(internalPeaks, fittedValues, yScale),
    };
}
//# sourceMappingURL=index.js.map